/**
 * @copyright 2015 CBC/Radio-Canada.
 * @license Unlicensed. All rights reserved.
 * @author Kristen Elliott [kristen.elliott@cbc.ca]
 */

import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import LoadMore from "./components/LoadMore/component";
import DefaultPolicy from "../ContentArea/library/DefaultPolicy";
import classNames from "classnames";
import { normalizeClass } from "../../utils/path/library";
import { DISPLAY_TYPES } from "./constants";
import useMounted from "../../hooks/useMounted";

const displayList = (classNames, formattedItems) => {
    return (
        <ul className={`contentListCards ${classNames}`}>
            {formattedItems.map((child) => (
                <li>{child}</li>
            ))}
        </ul>
    );
};

const getDisplayType = (type, formattedItems, listAriaLabel = null) => {
    switch (type) {
        case DISPLAY_TYPES.DISPLAY_FOUR:
            return displayList(type, formattedItems);
        case DISPLAY_TYPES.DISPLAY_FOUR_RIGHT_BORDER:
            return displayList(type, formattedItems);
        case DISPLAY_TYPES.TYPE_LIST:
            return (
                <ul
                    className="listDisplay contentListCards"
                    data-cy="topStories"
                    aria-label={listAriaLabel}
                >
                    {formattedItems}
                </ul>
            );
        case DISPLAY_TYPES.TYPE_LIST_ORDERED:
            return (
                <ol
                    className="listDisplay contentListCards"
                    data-cy="topStories"
                    aria-label={listAriaLabel}
                >
                    {formattedItems}
                </ol>
            );

        default:
            return (
                <div className="contentListCards" data-cy="topStories">
                    {formattedItems}
                </div>
            );
    }
};

/**
 * Component displays list of content for a given category.
 *
 * @param {object} props Component properties
 * @returns {React.Component} The ContentList component
 */
export default function ContentList({
    items = null,
    baseSection = "",
    subSection = "",
    loadMore = true,
    trackingSection = "",
    contentListClasses,
    displayType = DISPLAY_TYPES.TYPE_DEFAULT,
    cardClickHandler = () => {},
    cardVariant,
    listAriaLabel,
    displayPolicy = new DefaultPolicy(),
    loadMoreFetch,
}) {
    const isMounted = useMounted();
    const [showLoadMore, setShowLoadMore] = useState(
        loadMore && items.length > 0
    );

    useEffect(() => {
        if (!isMounted) return;
        setShowLoadMore(loadMore);
    }, [loadMore]);

    const loadMoreButton = showLoadMore ? (
        <LoadMore fetchFunction={loadMoreFetch} />
    ) : null;

    // Apply the given display policy to the provided items in order to
    // generate the sequence of React elements to display.
    const sections = {
        baseSection,
        subSection,
        trackingSection,
    };

    const formattedItems = displayPolicy.format(
        items,
        sections,
        cardClickHandler,
        displayType,
        cardVariant
    );
    const normalizedTrackingSection = normalizeClass("sclt", trackingSection);

    //New prop contentListClasses allows for extra CSS classes to configure the content list from it's parent.
    //Currently the class options include hideContentBottom to hide the metadatatext.
    const classes = classNames(
        "contentList",
        `${normalizedTrackingSection}`,
        contentListClasses
    );

    const contentCards = getDisplayType(
        displayType,
        formattedItems,
        listAriaLabel
    );

    return (
        <div className={classes} data-testid="content-list">
            {contentCards}
            {loadMoreButton}
        </div>
    );
}

ContentList.propTypes = {
    items: PropTypes.array.isRequired,
    baseSection: PropTypes.string,
    subSection: PropTypes.string,

    // OPTIONAL
    displayPolicy: PropTypes.object,
    displayType: PropTypes.oneOf(Object.values(DISPLAY_TYPES)),
    loadMore: PropTypes.bool,
    trackingSection: PropTypes.string,
    contentListClasses: PropTypes.string,
    cardVariant: PropTypes.string,
};
